import { queryOptions, useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';

import { artiaProtectedApi } from '@/lib/api-client';
import { CoverageTagLookup } from '@/types/coverage-tag-lookup';

export const makeCoverageTagQueryKey = (id?: number | string) => ['coverage-tag', `${id}`];

const getCoverageTag = async (id: number | string): Promise<CoverageTagLookup> => {
  return await artiaProtectedApi.get(`coverage-tags/${id}`);
};

export const getCoverageTagOptions = (id?: number | string) => {
  return queryOptions({
    queryKey: makeCoverageTagQueryKey(id),
    queryFn: () => getCoverageTag(id!),
    enabled: !isNil(id)
  });
};

type UseGetCoverageTagOptions = {
  id?: number | string;
  queryConfig?: Parameters<typeof getCoverageTagOptions>;
};

export const useGetCoverageTag = ({ queryConfig, id }: UseGetCoverageTagOptions = {}) => {
  return useQuery({
    ...getCoverageTagOptions(id),
    ...queryConfig
  });
};

const getCoverageTagLookups = async ({ drugId, defaultOnly = false }: { drugId: number, defaultOnly?: boolean }): Promise<CoverageTagLookup[]> => {
  const queryParams = new URLSearchParams();

  if (!isNil(drugId)) {
    queryParams.set('drugId', drugId.toString());
  }

  if (!isNil(defaultOnly)) {
    queryParams.set('defaultOnly', defaultOnly.toString());
  }

  const HACK_ALL_RPP = 1000000;

  queryParams.set('rpp', HACK_ALL_RPP.toString());
  const { items } = await artiaProtectedApi.get('/lookups/coverage-tags', {
    params: queryParams
  }) as any;

  return items;
};

const getCoverageTagLookupsForMultipleDrugs = async ({ drugIds, defaultOnly = false }: { drugIds: number[], defaultOnly?: boolean }): Promise<CoverageTagLookup[]> => {
  if (drugIds.length === 0) {
    return [];
  }

  const queryParams = new URLSearchParams();

  // Add each drugId as a separate query parameter (drugIds=1&drugIds=2&drugIds=3)
  drugIds.forEach(drugId => {
    queryParams.append('drugIds', drugId.toString());
  });

  if (!isNil(defaultOnly)) {
    queryParams.set('defaultOnly', defaultOnly.toString());
  }

  const HACK_ALL_RPP = 1000000;
  queryParams.set('rpp', HACK_ALL_RPP.toString());

  const response = await artiaProtectedApi.get('/lookups/coverage-tags/multiple-drugs', {
    params: queryParams
  }) as any;

  // Handle both direct array response and paginated response format
  // The API might return data directly or wrapped in { items } depending on implementation
  if (Array.isArray(response)) {
    return response;
  } else if (response && response.items && Array.isArray(response.items)) {
    return response.items;
  } else {
    // Fallback to empty array if response format is unexpected
    console.warn('Unexpected response format from coverage tags API:', response);
    return [];
  }
};

type UseGetCoverageTagLookupsOptions = {
  drugId: number;
  queryConfig?: Parameters<typeof getCoverageTagLookups>;
}

export const getCoverageTagLookupQueryOptions = ({ drugId }: { drugId: number }) => {
  return queryOptions({
    queryKey: ['coverage-tag-lookups', drugId],
    queryFn: () => getCoverageTagLookups({ drugId })
  });
};

export const getCoverageTagLookupsForMultipleDrugsQueryOptions = ({ drugIds }: { drugIds: number[] }) => {
  return queryOptions({
    queryKey: ['coverage-tag-lookups-multiple', drugIds.sort().join(',')],
    queryFn: () => getCoverageTagLookupsForMultipleDrugs({ drugIds }),
    enabled: drugIds.length > 0
  });
};

export const useGetCoverageTagLookups = ({ drugId, queryConfig }: UseGetCoverageTagLookupsOptions) => {
  return useQuery({
    ...getCoverageTagLookupQueryOptions({ drugId }),
    ...queryConfig
  });
};

export const useGetCoverageTagLookupsForMultipleDrugs = ({ drugIds, queryConfig }: { drugIds: number[], queryConfig?: any }) => {
  return useQuery<CoverageTagLookup[], Error>({
    ...getCoverageTagLookupsForMultipleDrugsQueryOptions({ drugIds }),
    ...queryConfig
  });
};


